@if (loading) {
  <p class="empty">{{ 'admin.schedulers.loading' | adminTranslate }}</p>
} @else {
  <section class="panel">
    <header class="panel__header">
      <div>
        <h1>{{ 'admin.schedulers.title' | adminTranslate }}</h1>
        <p class="panel__lede">{{ 'admin.schedulers.lede' | adminTranslate }}</p>
      </div>
      <button
        type="button"
        class="btn-icon btn-icon-refresh"
        (click)="loadSchedulers()"
        [disabled]="loading"
        [title]="'admin.common.refresh' | adminTranslate"
        [attr.aria-label]="'admin.common.refresh' | adminTranslate">
        <ui-icon name="refresh"></ui-icon>
      </button>
    </header>

    <div class="scheduler-table-wrap">
      <table class="scheduler-table">
        <thead>
          <tr>
            <th>{{ 'admin.schedulers.table.scheduler' | adminTranslate }}</th>
            <th>{{ 'admin.schedulers.table.status' | adminTranslate }}</th>
            <th>{{ 'admin.schedulers.table.schedule' | adminTranslate }}</th>
            <th>{{ 'admin.schedulers.table.runtime' | adminTranslate }}</th>
            <th>{{ 'admin.schedulers.table.actions' | adminTranslate }}</th>
          </tr>
        </thead>
        <tbody>
          @for (scheduler of schedulers; track scheduler.key) {
            <tr>
              <td class="scheduler-table__cell scheduler-table__cell--scheduler">
                <div class="scheduler-main">
                  <strong>{{ scheduler.displayName }}</strong>
                  @if (scheduler.description) {
                    <span class="scheduler-description">{{ scheduler.description }}</span>
                  }
                  <span class="scheduler-key">{{ scheduler.key }}</span>
                  <div class="schedule-summary schedule-summary--inline">
                    <span class="schedule-summary__label">{{ 'admin.schedulers.summary.label' | adminTranslate }}</span>
                    <strong>{{ scheduleSummary(scheduler.key) }}</strong>
                  </div>
                </div>
              </td>
              <td class="scheduler-table__cell scheduler-table__cell--status">
                <ui-toggle
                  class="scheduler-toggle"
                  [checked]="drafts[scheduler.key].enabled"
                  (checkedChange)="drafts[scheduler.key].enabled = $event"
                  [label]="(drafts[scheduler.key].enabled ? ('admin.common.active' | adminTranslate) : ('admin.features.status.DISABLED' | adminTranslate))"
                  [description]="enabledDescription(drafts[scheduler.key].enabled)">
                </ui-toggle>
              </td>
              <td class="scheduler-table__cell scheduler-table__cell--schedule">
                <div class="schedule-grid">
                  <label>
                    <span>{{ 'admin.schedulers.fields.frequency' | adminTranslate }}</span>
                    <ui-select [(ngModel)]="drafts[scheduler.key].frequency">
                      @for (frequency of frequencies; track frequency) {
                        <option [value]="frequency">{{ frequencyLabel(frequency) | adminTranslate }}</option>
                      }
                    </ui-select>
                  </label>

                  <p class="schedule-hint">{{ scheduleModeHint(drafts[scheduler.key].frequency) }}</p>

                  @if (showInterval(drafts[scheduler.key].frequency)) {
                    <label>
                      <span>{{ frequencyIntervalLabel(drafts[scheduler.key].frequency) | adminTranslate }}</span>
                      <input type="number" min="1" max="999" [(ngModel)]="drafts[scheduler.key].intervalValue">
                    </label>
                  }

                  @if (showWeeklyFields(drafts[scheduler.key].frequency)) {
                    <div class="schedule-group">
                      <span class="schedule-group__title">{{ 'admin.schedulers.groups.when' | adminTranslate }}</span>
                      <label>
                        <span>{{ 'admin.schedulers.fields.dayOfWeek' | adminTranslate }}</span>
                        <ui-select [(ngModel)]="drafts[scheduler.key].dayOfWeek">
                          @for (day of weekDays; track day) {
                            <option [value]="day">{{ dayLabel(day) | adminTranslate }}</option>
                          }
                        </ui-select>
                      </label>
                    </div>
                  }

                  @if (showMonthlyFields(drafts[scheduler.key].frequency)) {
                    <div class="schedule-group">
                      <span class="schedule-group__title">{{ 'admin.schedulers.groups.when' | adminTranslate }}</span>
                      <label>
                        <span>{{ 'admin.schedulers.fields.monthlyPattern' | adminTranslate }}</span>
                        <ui-select [(ngModel)]="drafts[scheduler.key].monthlyPattern">
                          <option value="DAY_OF_MONTH">{{ 'admin.schedulers.patterns.DAY_OF_MONTH' | adminTranslate }}</option>
                          <option value="NTH_WEEKDAY">{{ 'admin.schedulers.patterns.NTH_WEEKDAY' | adminTranslate }}</option>
                          <option value="LAST_DAY">{{ 'admin.schedulers.patterns.LAST_DAY' | adminTranslate }}</option>
                        </ui-select>
                      </label>

                      @if (drafts[scheduler.key].monthlyPattern === 'NTH_WEEKDAY') {
                        <div class="schedule-inline">
                          <label>
                            <span>{{ 'admin.schedulers.fields.occurrence' | adminTranslate }}</span>
                            <ui-select [(ngModel)]="drafts[scheduler.key].weekdayOccurrence">
                              @for (occurrence of weekdayOccurrences; track occurrence) {
                                <option [value]="occurrence">{{ occurrenceLabel(occurrence) | adminTranslate }}</option>
                              }
                            </ui-select>
                          </label>

                          <label>
                            <span>{{ 'admin.schedulers.fields.dayOfWeek' | adminTranslate }}</span>
                            <ui-select [(ngModel)]="drafts[scheduler.key].dayOfWeek">
                              @for (day of weekDays; track day) {
                                <option [value]="day">{{ dayLabel(day) | adminTranslate }}</option>
                              }
                            </ui-select>
                          </label>
                        </div>
                      }

                      @if (drafts[scheduler.key].monthlyPattern === 'DAY_OF_MONTH') {
                        <label>
                          <span>{{ 'admin.schedulers.fields.dayOfMonth' | adminTranslate }}</span>
                          <input type="number" min="1" max="31" [(ngModel)]="drafts[scheduler.key].dayOfMonth">
                        </label>
                      }
                    </div>
                  }

                  @if (showYearlyFields(drafts[scheduler.key].frequency)) {
                    <div class="schedule-group">
                      <span class="schedule-group__title">{{ 'admin.schedulers.groups.when' | adminTranslate }}</span>
                      <label>
                        <span>{{ 'admin.schedulers.fields.yearlyPattern' | adminTranslate }}</span>
                        <ui-select [(ngModel)]="drafts[scheduler.key].yearlyPattern">
                          <option value="DATE">{{ 'admin.schedulers.patterns.DATE' | adminTranslate }}</option>
                          <option value="NTH_WEEKDAY">{{ 'admin.schedulers.patterns.NTH_WEEKDAY' | adminTranslate }}</option>
                        </ui-select>
                      </label>

                      <label>
                        <span>{{ 'admin.schedulers.fields.monthOfYear' | adminTranslate }}</span>
                        <ui-select [(ngModel)]="drafts[scheduler.key].monthOfYear">
                          @for (month of months; track month.value) {
                            <option [value]="month.value">{{ month.labelKey | adminTranslate }}</option>
                          }
                        </ui-select>
                      </label>

                      @if (drafts[scheduler.key].yearlyPattern === 'DATE') {
                        <label>
                          <span>{{ 'admin.schedulers.fields.dayOfMonth' | adminTranslate }}</span>
                          <input type="number" min="1" max="31" [(ngModel)]="drafts[scheduler.key].dayOfMonth">
                        </label>
                      } @else {
                        <div class="schedule-inline">
                          <label>
                            <span>{{ 'admin.schedulers.fields.occurrence' | adminTranslate }}</span>
                            <ui-select [(ngModel)]="drafts[scheduler.key].weekdayOccurrence">
                              @for (occurrence of weekdayOccurrences; track occurrence) {
                                <option [value]="occurrence">{{ occurrenceLabel(occurrence) | adminTranslate }}</option>
                              }
                            </ui-select>
                          </label>

                          <label>
                            <span>{{ 'admin.schedulers.fields.dayOfWeek' | adminTranslate }}</span>
                            <ui-select [(ngModel)]="drafts[scheduler.key].dayOfWeek">
                              @for (day of weekDays; track day) {
                                <option [value]="day">{{ dayLabel(day) | adminTranslate }}</option>
                              }
                            </ui-select>
                          </label>
                        </div>
                      }
                    </div>
                  }

                  @if (showTimeField(drafts[scheduler.key].frequency)) {
                    <div class="schedule-group">
                      <span class="schedule-group__title">{{ 'admin.schedulers.groups.time' | adminTranslate }}</span>
                      <label>
                        <span>{{ 'admin.schedulers.fields.timeOfDay' | adminTranslate }}</span>
                        <input type="time" [(ngModel)]="drafts[scheduler.key].timeOfDay">
                      </label>
                    </div>
                  }
                </div>
              </td>
              <td class="scheduler-table__cell scheduler-table__cell--runtime">
                <div class="runtime">
                  <span>{{ 'admin.schedulers.runtime.nextRun' | adminTranslate }}: {{ scheduler.nextRunAt ? (scheduler.nextRunAt | date:'dd/MM/yyyy HH:mm:ss') : '-' }}</span>
                  <span>{{ 'admin.schedulers.runtime.lastRun' | adminTranslate }}: {{ scheduler.lastRunAt ? (scheduler.lastRunAt | date:'dd/MM/yyyy HH:mm:ss') : '-' }}</span>
                  <span>{{ 'admin.schedulers.runtime.lastSuccess' | adminTranslate }}: {{ scheduler.lastSuccessAt ? (scheduler.lastSuccessAt | date:'dd/MM/yyyy HH:mm:ss') : '-' }}</span>
                  <span class="runtime-label">{{ 'admin.schedulers.runtime.lastStatus' | adminTranslate }}</span>
                  <span [class]="statusBadgeClass(scheduler)">{{ statusLabelKey(scheduler) | adminTranslate }}</span>
                  @if (scheduler.lastError) {
                    <span class="runtime-error">{{ scheduler.lastError }}</span>
                  }
                </div>
              </td>
              <td class="scheduler-table__cell scheduler-table__cell--actions">
                <div class="actions">
                  <button
                    type="button"
                    class="btn-icon scheduler-action scheduler-action--save"
                    [disabled]="savingKeys.has(scheduler.key)"
                    (click)="saveScheduler(scheduler)"
                    [title]="savingKeys.has(scheduler.key) ? ('admin.common.saving' | adminTranslate) : ('admin.common.save' | adminTranslate)"
                    [attr.aria-label]="savingKeys.has(scheduler.key) ? ('admin.common.saving' | adminTranslate) : ('admin.common.save' | adminTranslate)">
                    <ui-icon name="save"></ui-icon>
                  </button>
                  <button
                    type="button"
                    class="btn-icon scheduler-action scheduler-action--run"
                    [disabled]="runningKeys.has(scheduler.key)"
                    (click)="runScheduler(scheduler.key)"
                    [title]="runningKeys.has(scheduler.key) ? ('admin.common.loading' | adminTranslate) : ('admin.schedulers.runNow' | adminTranslate)"
                    [attr.aria-label]="runningKeys.has(scheduler.key) ? ('admin.common.loading' | adminTranslate) : ('admin.schedulers.runNow' | adminTranslate)">
                    <ui-icon name="rocket"></ui-icon>
                  </button>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>
}
